import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { ArrowLeftOutlined, SendOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import message from "@/utils/globalMessage";
import { SERVICE_REQUEST_STATUS_COLORS } from "./constants";
import "./styles.css";

export default function ServiceRequestDetail({
  requestId,
  listPath,
  loadRequest,
  addNote,
  canAddNote,
  closeRequest,
  reopenRequest,
}) {
  const navigate = useNavigate();
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const [record, setRecord] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);

  const loadRecord = useCallback(async () => {
    setLoading(true);
    try {
      const response = await loadRequest(requestId);
      setRecord(response.data);
    } catch (error) {
      message.error("Failed to load service request");
    } finally {
      setLoading(false);
    }
  }, [loadRequest, requestId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const submitNote = async () => {
    const content = note.trim();
    if (!content) {
      message.error("Please enter a note");
      return;
    }
    setSubmitting(true);
    try {
      await addNote(requestId, content);
      setNote("");
      setNoteModalOpen(false);
      await loadRecord();
      message.success("Note added successfully");
    } catch (error) {
      message.error(error?.response?.data?.status || "Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async () => {
    if (!record || (!closeRequest && !reopenRequest)) return;
    setChangingStatus(true);
    try {
      if (record.status === "closed") {
        await reopenRequest(record.id);
        message.success("Request reopened");
      } else {
        await closeRequest(record.id);
        message.success("Request closed");
      }
      await loadRecord();
    } catch (error) {
      message.error("Failed to update request status");
    } finally {
      setChangingStatus(false);
    }
  };

  const noteEnabled = Boolean(canAddNote && record?.status !== "closed");
  const canChangeStatus = Boolean(record && closeRequest && reopenRequest);
  const statusButton = canChangeStatus ? (
    <Button
      danger={record.status !== "closed"}
      onClick={changeStatus}
      loading={changingStatus}
    >
      {record.status === "closed" ? "Reopen" : "Close"}
    </Button>
  ) : null;
  const noteButton = (
    <Button
      type="primary"
      icon={<SendOutlined />}
      onClick={() => setNoteModalOpen(true)}
      disabled={!noteEnabled}
    >
      Add Note
    </Button>
  );

  return (
    <div className="service-request-detail-page">
      {isPC && (
        <div className="service-request-detail-header">
          <div className="service-request-detail-heading">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(listPath)} title="Back" />
            <Typography.Title level={4} style={{ margin: 0 }}>
              Service Request {record ? `#${record.id}` : ""}
            </Typography.Title>
            {record && (
              <Tag color={SERVICE_REQUEST_STATUS_COLORS[record.status]}>{record.status}</Tag>
            )}
          </div>
          <Space>
            {noteButton}
            {statusButton}
          </Space>
        </div>
      )}

      <Spin spinning={loading}>
        {record && (
          <>
            {!isPC && (
              <div className="service-request-mobile-status-action">
                <Space wrap>
                  {noteButton}
                  {statusButton}
                </Space>
              </div>
            )}
            <Form className="service-request-overview-form" layout="vertical">
              <Row gutter={[24, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item label="Title">
                    <Input value={record.title} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Facility">
                    <Input value={record.facility_name} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Company">
                    <Input value={record.company_name} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Suite / Area">
                    <Input value={record.suite_area} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Category">
                    <Input value={record.category_name} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Priority">
                    <Input value={record.priority} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Status">
                    <div className="service-request-readonly-field">
                      <Tag color={SERVICE_REQUEST_STATUS_COLORS[record.status]}>{record.status}</Tag>
                    </div>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Description">
                    <Input.TextArea value={record.description} rows={3} readOnly />
                  </Form.Item>
                </Col>
              </Row>

              <div className="service-request-notes-section">
                <div className="service-request-notes-heading">
                  Notes ({record.notes?.length || 0})
                </div>
                {record.notes?.length ? (
                  <List
                    className="service-request-notes-list"
                    dataSource={record.notes}
                    renderItem={(item) => (
                      <List.Item>
                        <List.Item.Meta
                          title={(
                            <Space wrap>
                              <Typography.Text strong>{item.createdby_name || "Unknown"}</Typography.Text>
                              <Typography.Text type="secondary">
                                {moment(item.created_date).format("YYYY-MM-DD HH:mm")}
                              </Typography.Text>
                            </Space>
                          )}
                          description={(
                            <Typography.Paragraph style={{ marginBottom: 0 }}>
                              {item.content}
                            </Typography.Paragraph>
                          )}
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notes" />
                )}
              </div>

              <Row gutter={[24, 0]} className="service-request-audit-row">
                <Col xs={24} md={12}>
                  <Form.Item label="Requested By">
                    <Input value={record.createdby_name} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Created Date">
                    <Input
                      value={moment(record.created_date).format("YYYY-MM-DD HH:mm")}
                      readOnly
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Modal
              title="Add Note"
              open={noteModalOpen}
              onCancel={() => {
                setNoteModalOpen(false);
                setNote("");
              }}
              onOk={submitNote}
              okText="Add Note"
              confirmLoading={submitting}
              okButtonProps={{ disabled: !note.trim() }}
              destroyOnClose
            >
              <Input.TextArea
                rows={5}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Enter note"
                maxLength={4000}
                showCount
              />
            </Modal>
          </>
        )}
      </Spin>
    </div>
  );
}
